import React, { Component, ErrorInfo, ReactNode } from 'react';
import { ErrorState } from 'components/shared/ErrorState';

// ---------------------------------------------------------------------------
// Props & State
// ---------------------------------------------------------------------------

export interface ComponentErrorBoundaryProps {
  /** Child components to render when no error has occurred. */
  children: ReactNode;
  /**
   * Human-readable label identifying which section crashed.
   * Used in console logs and as part of the error title shown to users.
   * @example "Attention Feed"
   */
  sectionName: string;
  /**
   * Size variant passed through to `<ErrorState>`.
   * @default "medium"
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Whether to wrap the fallback UI in a Paper container.
   * @default true
   */
  contained?: boolean;
}

interface State {
  hasError: boolean;
  error?: Error;
}

// ---------------------------------------------------------------------------
// ComponentErrorBoundary
// ---------------------------------------------------------------------------

/**
 * Lightweight, component-level error boundary designed for wrapping individual
 * dashboard sections (e.g. AttentionFeed, EventsIncidentsTabs, Video Wall grid).
 *
 * When a child component throws during render, this boundary:
 *  1. Catches the error and stores it in local state.
 *  2. Logs the error and component stack to `console.error`.
 *  3. Renders an `<ErrorState>` fallback with the section name and a retry
 *     button that resets the boundary, re-mounting the children.
 *
 * This differs from the route-level `<ErrorBoundary>` in
 * `components/errorBoundary/ErrorBoundary.jsx`, which catches crashes across
 * the entire page. `ComponentErrorBoundary` is intentionally scoped so that a
 * failure in one section does not take down the rest of the dashboard.
 *
 * @example
 * <ComponentErrorBoundary sectionName="Attention Feed">
 *   <AttentionFeed feed={feed} loading={false} />
 * </ComponentErrorBoundary>
 */
export class ComponentErrorBoundary extends Component<ComponentErrorBoundaryProps, State> {
  constructor(props: ComponentErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(
      `[CANvision][${this.props.sectionName}] Uncaught error in component tree:`,
      error,
      errorInfo.componentStack,
    );
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      const { sectionName, size = 'medium', contained = true } = this.props;

      return (
        <ErrorState
          title={`${sectionName} failed to load`}
          message={
            this.state.error?.message
              ? `Error: ${this.state.error.message}`
              : 'An unexpected error occurred. Click the button below to try again.'
          }
          onRetry={this.handleRetry}
          retryText="Try Again"
          size={size}
          contained={contained}
        />
      );
    }

    return this.props.children;
  }
}

export default ComponentErrorBoundary;
